import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Bell, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, CircleHelp, CopyCheck, Download, FileText, LucideAngularModule, LucideIconData, MoveLeft, Plus, ReceiptText, Users } from 'lucide-angular';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { Contract, LoaBillConfig, Vendor } from '../../../core/models/domain.models';
import { ApiService } from '../../../core/services/api.service';
import { ToastService } from '../../../shared/ui/toast.service';
import { ContractsService } from '../../contract/services/contracts.service';
import { LoaService } from '../../loa/services/loa.service';
import { VendorsService } from '../../vendor/services/vendors.service';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../../../core/constants/pagination.constants';

type OaBillingTab = 'unbilled' | 'bills' | 'mergeable';
type BillType = string;
type PaidBy = 'Both' | 'Buyer' | 'Seller';

interface ApplicationScheduleApi {
  from_date: string;
  to_date: string;
  quantum?: number | null;
}

interface ApplicationApi {
  id: string;
  application_number: string;
  contract_id?: string | null;
  application_type: string;
  application_date: string;
  route?: string | null;
  generation_source?: string | null;
  seller_vendor_id?: string | null;
  seller_loa_id?: string | null;
  buyer_vendor_id?: string | null;
  buyer_loa_id?: string | null;
  total_quantum?: number | null;
  status: string;
  schedules?: ApplicationScheduleApi[];
  approval_status?: string;
  approval_available?: boolean;
  approval_date?: string | null;
  approval_charges?: ApplicationChargeApi[];
}

interface ApplicationChargeApi {
  group_name: string;
  section_heading?: string | null;
  sub_group_name: string;
  rate_mwh?: number | null;
  quantum_mwh?: number | null;
  amount?: number | null;
}

interface StatCard {
  label: string;
  value: string;
  key: 'apps' | 'unbilled' | 'totalAmount' | 'pendingAmount' | 'mergeable';
}

interface GenerateChargeRow {
  chargeType: string;
  totalAmount: number;
  paidBy: PaidBy;
  buyerPercent: number;
  sellerPercent: number;
}

interface OaBillLineItem {
  description: string;
  hsnSac: string;
  quantity: string;
  rate: string;
  per: string;
  amount: number;
}

interface UnbilledRow {
  id: string;
  applicationId: string;
  contractRowId: string;
  contractId: string;
  type: BillType;
  buyerVendorId: string;
  sellerVendorId: string;
  buyerLoaId: string;
  sellerLoaId: string;
  billCycle: string;
  buyer: string;
  seller: string;
  buyerAddress: string;
  sellerAddress: string;
  buyerGst: string;
  sellerGst: string;
  buyerPan: string;
  sellerPan: string;
  buyerState: string;
  sellerState: string;
  source: string;
  routeFrom: string;
  routeTo: string;
  quantum: number;
  period: string;
  approvalDate: string;
  charges: GenerateChargeRow[];
  approvalStatus: string;
  approvalAvailable: boolean;
}

interface GeneratedInvoiceApi {
  row_key: string;
  bill_id: string;
  mode?: string | null;
  invoice_type?: string | null;
  bill_for?: string | null;
  invoice_date?: string | null;
  due_date?: string | null;
  rebate_date?: string | null;
  rebate_percentage?: number | null;
  row_json?: Record<string, unknown> | null;
}

interface OaGeneratedBill {
  rowKey: string;
  billId: string;
  party: 'Buyer' | 'Seller';
  type: BillType;
  invoiceDate: string;
  applicationId: string;
  contractRowId: string;
  contractId: string;
  buyer: string;
  seller: string;
  buyerAddress: string;
  sellerAddress: string;
  buyerGst: string;
  sellerGst: string;
  buyerPan: string;
  sellerPan: string;
  buyerState: string;
  sellerState: string;
  source: string;
  period: string;
  fromDate: string;
  toDate: string;
  quantum: number;
  lineItems: OaBillLineItem[];
  totalAmount: number;
}

const DEFAULT_CHARGE_TOTALS: Array<{ chargeType: string; totalAmount: number }> = [
  { chargeType: 'Transmission', totalAmount: 65000 },
  { chargeType: 'Wheeling', totalAmount: 25000 },
  { chargeType: 'SLDC', totalAmount: 12000 },
  { chargeType: 'RLDC', totalAmount: 6200 },
  { chargeType: 'App', totalAmount: 1400 },
  { chargeType: 'Loss', totalAmount: 5000 },
];

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './oa-billing.page.html',
  styleUrl: './oa-billing.page.scss',
})
export class OaBillingPage implements OnInit {
  protected readonly invoiceLogoUrl = '/sharajman-logo.svg';
  protected readonly issuerProfile = {
    companyName: 'SHARAJMAN TECHNOLOGY',
    address: '-',
    pan: 'AACCE4669H',
    gst: '29AACCE4669H1Z6',
    state: '-',
    stateCode: '-',
    cin: '-',
  } as const;
  protected readonly bankProfile = {
    accountHolderName: 'SHARAJMAN TECHNOLOGY',
    bankName: '-',
    accountNumber: '-',
    branch: '-',
    ifscCode: '-',
    swiftCode: '-',
  } as const;
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);
  private readonly contractsService = inject(ContractsService);
  private readonly loaService = inject(LoaService);
  private readonly vendorsService = inject(VendorsService);
  private readonly toast = inject(ToastService);

  protected readonly PlusIcon = Plus;
  protected readonly DownloadIcon = Download;
  protected readonly BellIcon = Bell;
  protected readonly ChevronsLeftIcon = ChevronsLeft;
  protected readonly ChevronLeftIcon = ChevronLeft;
  protected readonly ChevronRightIcon = ChevronRight;
  protected readonly ChevronsRightIcon = ChevronsRight;
  protected readonly UsersIcon = Users;
  protected readonly ReceiptTextIcon = ReceiptText;
  protected readonly CircleHelpIcon = CircleHelp;
  protected readonly FileTextIcon = FileText;
  protected readonly CopyCheckIcon = CopyCheck;
  protected readonly MoveLeftIcon = MoveLeft;

  protected readonly activeTab = signal<OaBillingTab>('unbilled');
  protected readonly loading = signal(false);
  protected readonly unbilledRows = signal<UnbilledRow[]>([]);
  protected readonly generatedBills = signal<OaGeneratedBill[]>([]);
  protected readonly selectedUnbilledRowKeys = signal<Record<string, boolean>>({});
  protected readonly showGenerateForm = signal(false);
  protected readonly selectedApplication = signal<UnbilledRow | null>(null);
  protected readonly chargeRows = signal<GenerateChargeRow[]>([]);
  protected readonly pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;
  protected readonly currentPage = signal(1);
  protected readonly pageSize = signal<number>(DEFAULT_PAGE_SIZE_OPTIONS[0] || 10);
  protected readonly totalRows = computed(() => {
    const tab = this.activeTab();
    if (tab === 'bills') return this.generatedBills().length;
    if (tab === 'unbilled') return this.unbilledRows().length;
    return 0;
  });
  protected readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalRows() / this.pageSize())));
  protected readonly currentPageSafe = computed(() => Math.min(this.currentPage(), this.totalPages()));
  protected readonly pageStartIndex = computed(() => (this.currentPageSafe() - 1) * this.pageSize());
  protected readonly pageStart = computed(() => (this.totalRows() ? this.pageStartIndex() + 1 : 0));
  protected readonly pageEnd = computed(() => Math.min(this.pageStartIndex() + this.pageSize(), this.totalRows()));
  protected readonly pagedGeneratedBills = computed(() => {
    const start = this.pageStartIndex();
    return this.generatedBills().slice(start, start + this.pageSize());
  });
  protected readonly pagedUnbilledRows = computed(() => {
    const start = this.pageStartIndex();
    return this.unbilledRows().slice(start, start + this.pageSize());
  });
  protected readonly totalApplications = computed(() => {
    const keys = new Set<string>();
    this.unbilledRows().forEach((row) => keys.add(row.applicationId || row.id));
    this.generatedBills().forEach((row) => keys.add(row.applicationId || row.rowKey));
    return keys.size;
  });

  protected readonly stats = computed<StatCard[]>(() => [
    { label: 'Total Applications', value: this.pad(this.totalApplications()), key: 'apps' },
    { label: 'Unbilled', value: this.pad(this.unbilledRows().length), key: 'unbilled' },
    {
      label: 'Total Bill Amount',
      value: this.formatCurrency(this.generatedBills().reduce((sum, item) => sum + item.totalAmount, 0)),
      key: 'totalAmount',
    },
    {
      label: 'Pending Bill Amount',
      value: this.formatCurrency(this.unbilledRows().reduce((sum, item) => sum + this.unbilledAmount(item), 0)),
      key: 'pendingAmount',
    },
    { label: 'Mergeable', value: this.pad(this.mergeableBills().length), key: 'mergeable' },
  ]);

  protected readonly tabCount = computed<Record<OaBillingTab, number>>(() => ({
    unbilled: this.unbilledRows().length,
    bills: this.generatedBills().length,
    mergeable: this.mergeableBills().length,
  }));

  protected readonly mergeableBills = computed(() => {
    const grouped = new Map<string, OaGeneratedBill[]>();
    this.generatedBills().forEach((item) => {
      const key = `${item.buyer}::${item.seller}::${item.party}`;
      const existing = grouped.get(key) || [];
      existing.push(item);
      grouped.set(key, existing);
    });
    return Array.from(grouped.values()).filter((items) => items.length > 1);
  });

  protected readonly alertTitle = computed(() => {
    const tab = this.activeTab();
    if (tab === 'bills') return 'Generated Buyer/Seller Bills';
    if (tab === 'mergeable') return 'Buyer-Seller Pairs with Multiple Applications';
    return `${this.unbilledRows().length} Approved Applications Awaiting Bill Generation`;
  });

  protected readonly alertSubtitle = computed(() => {
    const tab = this.activeTab();
    if (tab === 'bills') return 'Review generated OA bills and download documents';
    if (tab === 'mergeable') return 'Merge bills for same buyer-seller combinations';
    return 'Only applications with OA bill config on seller LOA and parsed approval data are shown';
  });

  protected readonly tableEmptyMessage = computed(() => {
    const tab = this.activeTab();
    if (tab === 'bills') return 'No generated OA bills found.';
    if (tab === 'mergeable') return 'Mergeable applications table design can be added next';
    return 'No applications available. Enable OA bill config in LOA configuration.';
  });

  protected readonly buyerTotal = computed(() =>
    this.chargeRows().reduce((sum, row) => sum + (row.totalAmount * row.buyerPercent) / 100, 0),
  );

  protected readonly sellerTotal = computed(() =>
    this.chargeRows().reduce((sum, row) => sum + (row.totalAmount * row.sellerPercent) / 100, 0),
  );

  protected readonly totalAmount = computed(() => this.chargeRows().reduce((sum, row) => sum + row.totalAmount, 0));
  protected readonly selectedUnbilledCount = computed(
    () => this.unbilledRows().filter((row) => this.isUnbilledRowSelected(row.id)).length,
  );
  protected readonly selectedUnbilledRows = computed(() =>
    this.unbilledRows().filter((row) => this.isUnbilledRowSelected(row.id)),
  );

  ngOnInit(): void {
    this.loadUnbilledApplications();
  }

  protected setTab(tab: OaBillingTab): void {
    this.activeTab.set(tab);
    this.currentPage.set(1);
  }

  protected typeClass(type: BillType): string {
    if (type === 'Daily') return 'type-daily';
    if (type === 'FCFS') return 'type-fcfs';
    if (type === 'Contingency') return 'type-contingency';
    return 'type-monthly';
  }

  protected cardIcon(card: StatCard): LucideIconData {
    if (card.key === 'totalAmount' || card.key === 'pendingAmount') return this.ReceiptTextIcon;
    if (card.key === 'mergeable') return this.UsersIcon;
    return this.BellIcon;
  }

  protected openGenerateForm(row: UnbilledRow): void {
    this.selectedApplication.set(row);
    this.chargeRows.set(row.charges.map((item) => ({ ...item })));
    this.showGenerateForm.set(true);
  }

  protected openGenerateFormForSelection(): void {
    if (this.activeTab() !== 'unbilled') {
      this.toast.show('Select an unbilled OA row to generate bills', '', 'info');
      return;
    }

    const selected = this.selectedUnbilledRows();
    if (!selected.length) {
      this.toast.show('Select at least one bill', '', 'info');
      return;
    }

    const first = selected[0];
    if (!first.approvalAvailable) {
      this.toast.show('Approval not available for the selected application', '', 'danger');
      return;
    }

    this.openGenerateForm(first);
  }

  protected openContract(row: UnbilledRow | OaGeneratedBill): void {
    if (!row.contractRowId || row.contractRowId === '-') return;
    this.router.navigateByUrl(`/contracts/${row.contractRowId}`);
  }

  protected closeGenerateForm(): void {
    this.showGenerateForm.set(false);
    this.selectedApplication.set(null);
    this.chargeRows.set([]);
  }

  protected onPaidByChange(index: number, value: PaidBy): void {
    this.chargeRows.update((rows) =>
      rows.map((row, idx) => {
        if (idx !== index) return row;
        if (value === 'Buyer') return { ...row, paidBy: value, buyerPercent: 100, sellerPercent: 0 };
        if (value === 'Seller') return { ...row, paidBy: value, buyerPercent: 0, sellerPercent: 100 };
        return { ...row, paidBy: value, buyerPercent: 50, sellerPercent: 50 };
      }),
    );
  }

  protected buyerAmount(row: GenerateChargeRow): number {
    return (row.totalAmount * row.buyerPercent) / 100;
  }

  protected sellerAmount(row: GenerateChargeRow): number {
    return (row.totalAmount * row.sellerPercent) / 100;
  }

  protected formatCurrency(value: number): string {
    return `₹ ${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(value || 0)}`;
  }

  protected unbilledAmount(row: UnbilledRow): number {
    if (!row.approvalAvailable) return 0;
    return (row.charges || []).reduce((sum, item) => sum + (Number(item.totalAmount) || 0), 0);
  }

  protected generateBillDisabledReason(row: UnbilledRow): string {
    if (row.approvalAvailable) return 'Generate buyer and seller OA bills';
    const status = (row.approvalStatus || '').trim();
    return status ? `Disabled because approval is not available. Status: ${status}` : 'Disabled because approval is not available for this application';
  }

  protected applicationBadge(index: number): string {
    return `A${String(index + 1).padStart(2, '0')}`;
  }

  protected generateBill(): void {
    const selected = this.selectedApplication();
    if (!selected || !selected.approvalAvailable) {
      this.toast.show('Approval not available for this application', '', 'danger');
      return;
    }

    const buyerTotal = this.buyerTotal();
    const sellerTotal = this.sellerTotal();
    const invoiceDate = this.toDdMmYyyy(new Date().toISOString().slice(0, 10));
    const dueDate = this.toDdMmYyyy(this.addDays(new Date(), 7).toISOString().slice(0, 10));
    const items: Array<{
      row_key: string;
      bill_id: string;
      mode: string;
      invoice_type: string;
      bill_for: string;
      invoice_date: string;
      due_date: string;
      rebate_date: string | null;
      rebate_percentage: number | null;
      row_json: Record<string, unknown>;
    }> = [];
    let nextSerial = this.nextOaBillSequence();

    if (sellerTotal > 0) {
      const billId = this.buildOaBillId(nextSerial++);
      const periodRange = this.periodDateRange(selected.period);
      const sellerLineItems = this.buildBillLineItems('Seller', selected);
      items.push({
        row_key: `${selected.id}::oa::seller`,
        bill_id: billId,
        mode: 'oa',
        invoice_type: 'OA',
        bill_for: 'SELLER',
        invoice_date: invoiceDate,
        due_date: dueDate,
        rebate_date: null,
        rebate_percentage: null,
        row_json: {
          row_key: `${selected.id}::oa::seller`,
          bill_id: billId,
          party: 'Seller',
          invoice_date: invoiceDate,
          due_date: dueDate,
          application_id: selected.applicationId,
          contract_row_id: selected.contractRowId,
          contract_id: selected.contractId,
          buyer: selected.buyer,
          seller: selected.seller,
          buyer_address: selected.buyerAddress,
          seller_address: selected.sellerAddress,
          buyer_gst: selected.buyerGst,
          seller_gst: selected.sellerGst,
          buyer_pan: selected.buyerPan,
          seller_pan: selected.sellerPan,
          buyer_state: selected.buyerState,
          seller_state: selected.sellerState,
          source: selected.source,
          billing_type: selected.type,
          period: selected.period,
          from_date: periodRange.from,
          to_date: periodRange.to,
          quantum: selected.quantum,
          line_items: sellerLineItems,
          total_amount: sellerTotal,
        },
      });
    }

    if (buyerTotal > 0) {
      const billId = this.buildOaBillId(nextSerial++);
      const periodRange = this.periodDateRange(selected.period);
      const buyerLineItems = this.buildBillLineItems('Buyer', selected);
      items.push({
        row_key: `${selected.id}::oa::buyer`,
        bill_id: billId,
        mode: 'oa',
        invoice_type: 'OA',
        bill_for: 'BUYER',
        invoice_date: invoiceDate,
        due_date: dueDate,
        rebate_date: null,
        rebate_percentage: null,
        row_json: {
          row_key: `${selected.id}::oa::buyer`,
          bill_id: billId,
          party: 'Buyer',
          invoice_date: invoiceDate,
          due_date: dueDate,
          application_id: selected.applicationId,
          contract_row_id: selected.contractRowId,
          contract_id: selected.contractId,
          buyer: selected.buyer,
          seller: selected.seller,
          buyer_address: selected.buyerAddress,
          seller_address: selected.sellerAddress,
          buyer_gst: selected.buyerGst,
          seller_gst: selected.sellerGst,
          buyer_pan: selected.buyerPan,
          seller_pan: selected.sellerPan,
          buyer_state: selected.buyerState,
          seller_state: selected.sellerState,
          source: selected.source,
          billing_type: selected.type,
          period: selected.period,
          from_date: periodRange.from,
          to_date: periodRange.to,
          quantum: selected.quantum,
          line_items: buyerLineItems,
          total_amount: buyerTotal,
        },
      });
    }

    if (!items.length) {
      this.toast.show('No OA amount available for billing', '', 'danger');
      return;
    }

    this.api.post<{ message: string }>('/api/invoices/generated', items).subscribe({
      next: () => {
        this.closeGenerateForm();
        this.setTab('bills');
        this.loadUnbilledApplications();
        this.toast.show('OA bills generated', '', 'success');
      },
      error: () => this.toast.show('Failed to save OA bills', '', 'danger'),
    });
  }

  protected goToPreviousPage(): void {
    if (this.currentPageSafe() <= 1) return;
    this.currentPage.set(this.currentPageSafe() - 1);
  }

  protected goToNextPage(): void {
    if (this.currentPageSafe() >= this.totalPages()) return;
    this.currentPage.set(this.currentPageSafe() + 1);
  }

  protected onPageSizeChange(value: string): void {
    const parsed = Number(value);
    const nextSize = this.pageSizeOptions.find((size) => size === parsed) ?? this.pageSizeOptions[0] ?? 10;
    this.pageSize.set(nextSize);
    this.currentPage.set(1);
  }

  protected isUnbilledRowSelected(rowKey: string): boolean {
    return !!this.selectedUnbilledRowKeys()[rowKey];
  }

  protected toggleUnbilledRowSelection(rowKey: string, checked: boolean): void {
    const row = this.unbilledRows().find((item) => item.id === rowKey);
    if (!row) {
      this.selectedUnbilledRowKeys.update((current) => ({ ...current, [rowKey]: checked }));
      return;
    }

    const targetRows = this.resolveSelectableRowsForUnbilledRow(row);
    this.selectedUnbilledRowKeys.update(() => {
      const next: Record<string, boolean> = {};
      if (!checked) return next;
      targetRows.forEach((item) => {
        next[item.id] = true;
      });
      return next;
    });
  }

  protected canSelectUnbilledRow(row: UnbilledRow): boolean {
    return (this.unbilledRowGroups().get(this.unbilledRowMatchKey(row)) || []).length > 1;
  }

  protected isUnbilledSelectAllActive(): boolean {
    const eligibleRows = this.unbilledRows().filter((row) => this.canSelectUnbilledRow(row));
    if (!eligibleRows.length) return false;
    const targetRows = this.resolveSelectableUnbilledGroup(eligibleRows);
    return !!targetRows.length && targetRows.every((row) => this.isUnbilledRowSelected(row.id));
  }

  protected toggleSelectAllUnbilledRows(): void {
    const eligibleRows = this.unbilledRows().filter((row) => this.canSelectUnbilledRow(row));
    if (!eligibleRows.length) {
      this.toast.show('No unbilled rows share the same buyer, seller, LOAs, and bill cycle', '', 'info');
      return;
    }

    const targetRows = this.resolveSelectableUnbilledGroup(eligibleRows);
    if (!targetRows.length) {
      this.toast.show('No unbilled rows share the same buyer, seller, LOAs, and bill cycle', '', 'info');
      return;
    }

    const allSelected = targetRows.every((row) => this.isUnbilledRowSelected(row.id));
    this.selectedUnbilledRowKeys.update((current) => {
      const next: Record<string, boolean> = {};
      targetRows.forEach((row) => {
        next[row.id] = !allSelected;
      });
      return next;
    });
  }

  private loadUnbilledApplications(): void {
    this.loading.set(true);

    forkJoin({
      applications: this.api.get<ApplicationApi[]>('/api/applications').pipe(catchError(() => of([]))),
      contracts: this.contractsService.list().pipe(catchError(() => of([] as Contract[]))),
      vendors: this.vendorsService.list().pipe(catchError(() => of([] as Vendor[]))),
      generated: this.api.get<GeneratedInvoiceApi[]>('/api/invoices/generated').pipe(catchError(() => of([]))),
    })
      .pipe(
        switchMap(({ applications, contracts, vendors, generated }) => {
          const loaIds = new Set<string>();
          applications.forEach((app) => {
            if (app.seller_loa_id) loaIds.add(String(app.seller_loa_id));
          });

          const list = Array.from(loaIds);
          if (!list.length) {
            return of({ applications, contracts, vendors, generated, configsByLoa: {} as Record<string, LoaBillConfig[]> });
          }

          return forkJoin(
            list.map((loaId) =>
              this.loaService
                .listBillConfigs(loaId)
                .pipe(catchError(() => of([] as LoaBillConfig[])), map((configs) => ({ loaId, configs }))),
            ),
          ).pipe(
            map((configRows) => {
              const configsByLoa = configRows.reduce<Record<string, LoaBillConfig[]>>((acc, item) => {
                acc[item.loaId] = item.configs || [];
                return acc;
              }, {});
              return { applications, contracts, vendors, generated, configsByLoa };
            }),
          );
        }),
      )
      .subscribe({
        next: ({ applications, contracts, vendors, generated, configsByLoa }) => {
          const contractsById = new Map(contracts.map((item) => [String(item.id), item]));
          const vendorMap = new Map(vendors.map((item) => [String(item.id), item.vendor_name]));
          const vendorsById = new Map(vendors.map((item) => [String(item.id), item]));
          const applicationsById = new Map(applications.map((item) => [String(item.id), item]));
          const applicationsByNumber = new Map(applications.map((item) => [String(item.application_number || item.id), item]));
          const generatedApplicationIds = new Set(
            (generated || [])
              .filter((item) => (item.invoice_type || '').toUpperCase() === 'OA')
              .map((item) => String(item.row_json?.['application_id'] || ''))
              .filter((item) => !!item),
          );
          const rows = applications
            .filter((app) => this.isEligibleOaApplicationStatus(app.status))
            .filter((app) => !generatedApplicationIds.has(app.application_number || String(app.id)))
            .map((app) => {
              const sellerLoaId = String(app.seller_loa_id || '');
              const buyerLoaId = String(app.buyer_loa_id || '');
              const oaConfig = this.resolveOaConfig(app, configsByLoa);

              const allocations = this.parseOaAllocation(oaConfig?.oa_charge_allocation || null, app.approval_charges || []);
              const period = this.periodText(app.schedules || [], app.application_date);
              const route = this.routeText(app.route);

              return {
                id: String(app.id),
                applicationId: app.application_number || String(app.id),
                contractRowId: String(app.contract_id || '-'),
                contractId: contractsById.get(String(app.contract_id || ''))?.contract_number || String(app.contract_id || '-'),
                type: this.resolveOaBillingType(oaConfig, app.application_type),
                buyerVendorId: String(app.buyer_vendor_id || ''),
                sellerVendorId: String(app.seller_vendor_id || ''),
                buyerLoaId: buyerLoaId,
                sellerLoaId: sellerLoaId,
                billCycle: String(oaConfig?.bill_cycle || '').trim() || '-',
                buyer: vendorMap.get(String(app.buyer_vendor_id || '')) || '-',
                seller: vendorMap.get(String(app.seller_vendor_id || '')) || '-',
                buyerAddress:
                  vendorsById.get(String(app.buyer_vendor_id || ''))?.billing_address ||
                  vendorsById.get(String(app.buyer_vendor_id || ''))?.registered_address ||
                  '-',
                sellerAddress:
                  vendorsById.get(String(app.seller_vendor_id || ''))?.billing_address ||
                  vendorsById.get(String(app.seller_vendor_id || ''))?.registered_address ||
                  '-',
                buyerGst: vendorsById.get(String(app.buyer_vendor_id || ''))?.gst_number || '-',
                sellerGst: vendorsById.get(String(app.seller_vendor_id || ''))?.gst_number || '-',
                buyerPan: vendorsById.get(String(app.buyer_vendor_id || ''))?.pan_number || '-',
                sellerPan: vendorsById.get(String(app.seller_vendor_id || ''))?.pan_number || '-',
                buyerState: vendorsById.get(String(app.buyer_vendor_id || ''))?.state || '-',
                sellerState: vendorsById.get(String(app.seller_vendor_id || ''))?.state || '-',
                source: app.generation_source || '-',
                routeFrom: route.from,
                routeTo: route.to,
                quantum: this.totalQuantum(app),
                period,
                approvalDate: this.toDdMmYyyy(app.approval_date || app.application_date),
                charges: allocations,
                approvalStatus: app.approval_status || (app.approval_available ? 'Available' : 'Approval Not Available'),
                approvalAvailable: !!app.approval_available,
              } as UnbilledRow;
            })
            .filter((item): item is UnbilledRow => !!item);

          this.unbilledRows.set(rows);
          this.selectedUnbilledRowKeys.set({});
          this.generatedBills.set(
            (generated || [])
              .filter((item) => (item.invoice_type || '').toUpperCase() === 'OA')
              .map((item) => {
                const appKey = String(item.row_json?.['application_id'] || '');
                const app = applicationsByNumber.get(appKey) || applicationsById.get(appKey);
                const sellerLoaId = String(app?.seller_loa_id || '');
                const sellerOaConfig = sellerLoaId ? this.latestOaConfig(configsByLoa[sellerLoaId] || []) : null;
                const buyerVendor = app ? vendorsById.get(String(app.buyer_vendor_id || '')) : null;
                const sellerVendor = app ? vendorsById.get(String(app.seller_vendor_id || '')) : null;
                const period = String(item.row_json?.['period'] || (app ? this.periodText(app.schedules || [], app.application_date) : '-'));
                const periodRange = this.periodDateRange(period);
                return {
                  rowKey: item.row_key,
                  billId: item.bill_id,
                  party: String(item.row_json?.['party'] || 'Buyer') === 'Seller' ? 'Seller' : 'Buyer',
                  type: String(item.row_json?.['billing_type'] || '') || this.resolveOaBillingType(sellerOaConfig, app?.application_type || ''),
                  invoiceDate: String(item.row_json?.['invoice_date'] || '-'),
                  applicationId: appKey,
                  contractRowId: String(item.row_json?.['contract_row_id'] || app?.contract_id || '-'),
                  contractId:
                    String(item.row_json?.['contract_number'] || '') ||
                    contractsById.get(String(app?.contract_id || item.row_json?.['contract_id'] || ''))?.contract_number ||
                    String(item.row_json?.['contract_id'] || app?.contract_id || '-'),
                  buyer: String(item.row_json?.['buyer'] || buyerVendor?.vendor_name || '-'),
                  seller: String(item.row_json?.['seller'] || sellerVendor?.vendor_name || '-'),
                  buyerAddress: String(item.row_json?.['buyer_address'] || buyerVendor?.billing_address || buyerVendor?.registered_address || '-'),
                  sellerAddress: String(item.row_json?.['seller_address'] || sellerVendor?.billing_address || sellerVendor?.registered_address || '-'),
                  buyerGst: String(item.row_json?.['buyer_gst'] || buyerVendor?.gst_number || '-'),
                  sellerGst: String(item.row_json?.['seller_gst'] || sellerVendor?.gst_number || '-'),
                  buyerPan: String(item.row_json?.['buyer_pan'] || buyerVendor?.pan_number || '-'),
                  sellerPan: String(item.row_json?.['seller_pan'] || sellerVendor?.pan_number || '-'),
                  buyerState: String(item.row_json?.['buyer_state'] || buyerVendor?.state || '-'),
                  sellerState: String(item.row_json?.['seller_state'] || sellerVendor?.state || '-'),
                  source: String(item.row_json?.['source'] || app?.generation_source || '-'),
                  period,
                  fromDate: String(item.row_json?.['from_date'] || periodRange.from),
                  toDate: String(item.row_json?.['to_date'] || periodRange.to),
                  quantum: Number(item.row_json?.['quantum']) || (app ? this.totalQuantum(app) : 0),
                  lineItems: this.parseLineItems(item.row_json?.['line_items']),
                  totalAmount: Number(item.row_json?.['total_amount']) || 0,
                } as OaGeneratedBill;
              }),
          );
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.unbilledRows.set([]);
          this.toast.show('Failed to load OA billing applications', '', 'danger');
        },
      });
  }

  private unbilledRowGroups(): Map<string, UnbilledRow[]> {
    const grouped = new Map<string, UnbilledRow[]>();
    this.unbilledRows().forEach((row) => {
      const key = this.unbilledRowMatchKey(row);
      const existing = grouped.get(key) || [];
      existing.push(row);
      grouped.set(key, existing);
    });
    return grouped;
  }

  private resolveSelectableUnbilledGroup(eligibleRows: UnbilledRow[]): UnbilledRow[] {
    const selected = eligibleRows.find((row) => this.isUnbilledRowSelected(row.id));
    const seed = selected || eligibleRows[0];
    if (!seed) return [];
    const key = this.unbilledRowMatchKey(seed);
    return this.unbilledRowGroups().get(key) || [];
  }

  private resolveSelectableRowsForUnbilledRow(row: UnbilledRow): UnbilledRow[] {
    const grouped = this.unbilledRowGroups().get(this.unbilledRowMatchKey(row)) || [];
    return grouped.length ? grouped : [row];
  }

  private unbilledRowMatchKey(row: UnbilledRow): string {
    return [
      row.buyerVendorId || row.buyer,
      row.sellerVendorId || row.seller,
      row.buyerLoaId || '-',
      row.sellerLoaId || '-',
      this.normalizeMatchValue(row.billCycle),
    ].join('::');
  }

  private normalizeMatchValue(value: string | null | undefined): string {
    return String(value || '')
      .trim()
      .toLowerCase();
  }

  private latestOaConfig(configs: LoaBillConfig[]): LoaBillConfig | null {
    const rows = (configs || [])
      .filter((item) => (item.bill_type || '').toUpperCase() === 'OA')
      .sort((a, b) => (b.effective_date || '').localeCompare(a.effective_date || ''));
    return rows[0] || null;
  }

  private resolveOaConfig(
    app: Pick<ApplicationApi, 'seller_loa_id' | 'buyer_loa_id'>,
    configsByLoa: Record<string, LoaBillConfig[]>,
  ): LoaBillConfig | null {
    const loaIds = [String(app.seller_loa_id || '').trim(), String(app.buyer_loa_id || '').trim()].filter((id) => !!id);
    for (const loaId of loaIds) {
      const match = this.latestOaConfig(configsByLoa[loaId] || []);
      if (match) return match;
    }
    return null;
  }

  private isEligibleOaApplicationStatus(status: string | null | undefined): boolean {
    const normalized = String(status || '')
      .trim()
      .toLowerCase();
    return ['approved', 'punched', 'active', 'final_approved', 'completed'].includes(normalized);
  }

  private parseOaAllocation(raw: string | null, approvalCharges: ApplicationChargeApi[]): GenerateChargeRow[] {
    const chargeTotals = this.resolveApprovalChargeTotals(approvalCharges);
    const defaults = chargeTotals.map((item) => ({
      chargeType: item.chargeType,
      totalAmount: item.totalAmount,
      paidBy: 'Both' as PaidBy,
      buyerPercent: 50,
      sellerPercent: 50,
    }));

    if (!raw || !raw.trim()) return defaults;

    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return defaults;
      const byType = new Map<string, GenerateChargeRow>();

      for (const row of parsed) {
        if (!row || typeof row !== 'object') continue;
        const source = row as Record<string, unknown>;
        const chargeType = String(source['charge_type'] || '').trim();
        if (!chargeType) continue;
        const normalizedChargeType = this.normalizeOaAllocationChargeType(chargeType);

        const payer = String(source['payer'] || 'Shared');
        const paidBy: PaidBy = payer === 'Buyer' ? 'Buyer' : payer === 'Seller' ? 'Seller' : 'Both';
        const buyerPercent = Number(source['buyer_percent']);
        const sellerPercent = Number(source['seller_percent']);

        byType.set(normalizedChargeType, {
          chargeType,
          totalAmount: defaults.find((item) => this.normalizeOaAllocationChargeType(item.chargeType) === normalizedChargeType)?.totalAmount || 0,
          paidBy,
          buyerPercent: Number.isFinite(buyerPercent) ? buyerPercent : paidBy === 'Buyer' ? 100 : paidBy === 'Seller' ? 0 : 50,
          sellerPercent: Number.isFinite(sellerPercent) ? sellerPercent : paidBy === 'Seller' ? 100 : paidBy === 'Buyer' ? 0 : 50,
        });
      }

      return defaults.map((item) => byType.get(this.normalizeOaAllocationChargeType(item.chargeType)) || item);
    } catch {
      return defaults;
    }
  }

  private resolveApprovalChargeTotals(charges: ApplicationChargeApi[]): Array<{ chargeType: string; totalAmount: number }> {
    if (!charges.length) return DEFAULT_CHARGE_TOTALS;
    return charges.map((item, index) => ({
      chargeType: this.normalizeChargeType(item, index),
      totalAmount: Number(item.amount) || (Number(item.rate_mwh) || 0) * (Number(item.quantum_mwh) || 0),
    }));
  }

  private normalizeChargeType(item: ApplicationChargeApi, index: number): string {
    const raw = `${item.sub_group_name || ''} ${item.group_name || ''} ${item.section_heading || ''}`.trim();
    if (!raw) return `Charge ${index + 1}`;
    const normalized = raw.toUpperCase();
    if (normalized.includes('TRANSMISSION')) return 'Transmission';
    if (normalized.includes('WHEEL')) return 'Wheeling';
    if (normalized.includes('SLDC')) return 'SLDC';
    if (normalized.includes('RLDC')) return 'RLDC';
    if (normalized.includes('LOSS')) return 'Loss';
    if (normalized.includes('APP')) return 'App';
    return raw;
  }

  private normalizeOaAllocationChargeType(value: string): string {
    const normalized = String(value || '')
      .trim()
      .toUpperCase()
      .replace(/CHARGES?/g, '')
      .replace(/FEES?/g, 'APP')
      .replace(/LOSSES?/g, 'LOSS')
      .replace(/\s+/g, ' ');

    if (normalized.includes('TRANSMISSION')) return 'TRANSMISSION';
    if (normalized.includes('WHEEL')) return 'WHEELING';
    if (normalized.includes('SLDC')) return 'SLDC';
    if (normalized.includes('RLDC')) return 'RLDC';
    if (normalized.includes('LOSS')) return 'LOSS';
    if (normalized.includes('APP')) return 'APP';
    return normalized;
  }

  private totalQuantum(app: ApplicationApi): number {
    const scheduleQuantum = (app.schedules || []).reduce((sum, item) => sum + (Number(item.quantum) || 0), 0);
    return scheduleQuantum || Number(app.total_quantum) || 0;
  }

  private periodText(schedules: ApplicationScheduleApi[], fallbackDate: string): string {
    if (!schedules.length) {
      const date = this.toDdMmYyyy(fallbackDate);
      return `${date} to ${date}`;
    }
    const from = [...schedules].sort((a, b) => (a.from_date || '').localeCompare(b.from_date || ''))[0]?.from_date || fallbackDate;
    const to = [...schedules].sort((a, b) => (b.to_date || '').localeCompare(a.to_date || ''))[0]?.to_date || fallbackDate;
    return `${this.toDdMmYyyy(from)} to ${this.toDdMmYyyy(to)}`;
  }

  private routeText(raw: string | null | undefined): { from: string; to: string } {
    const value = (raw || '').trim();
    if (!value) return { from: '-', to: '-' };
    const split = value.includes('->') ? value.split('->') : value.split(',');
    if (split.length < 2) return { from: value, to: '-' };
    return { from: split[0].trim() || '-', to: split.slice(1).join(',').trim() || '-' };
  }

  private normalizeType(type: string): BillType {
    const value = (type || '').trim().toUpperCase();
    if (value.includes('MONTH')) return 'Monthly';
    if (value.includes('DAY')) return 'Daily';
    if (value.includes('FCFS') || value.includes('FCFC')) return 'FCFS';
    if (value.includes('CONTINGENCY')) return 'Contingency';
    return 'Other';
  }

  private resolveOaBillingType(config: LoaBillConfig | null | undefined, fallbackType: string | null | undefined): BillType {
    const cycle = String(config?.bill_cycle || '').trim();
    if (!cycle) return this.normalizeType(fallbackType || '');
    if (cycle.startsWith('Custom:')) return 'Custom';
    if (/^approval\s*wise$/i.test(cycle)) return 'Approval Wise';
    return cycle;
  }

  private toDdMmYyyy(value: string | null | undefined): string {
    const raw = (value || '').trim();
    if (!raw) return '-';
    if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) return raw;
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [y, m, d] = raw.split('-');
      return `${d}-${m}-${y}`;
    }
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return raw;
    const d = String(parsed.getDate()).padStart(2, '0');
    const m = String(parsed.getMonth() + 1).padStart(2, '0');
    const y = parsed.getFullYear();
    return `${d}-${m}-${y}`;
  }

  private pad(value: number): string {
    return String(Math.max(value, 0)).padStart(2, '0');
  }

  protected downloadGeneratedBill(bill: OaGeneratedBill): void {
    const html = this.buildOaBillDocumentHtml(bill);
    const printWindow = window.open('', '_blank', 'width=1024,height=768');
    if (!printWindow) {
      this.toast.show('Enable popups to download bill as PDF', '', 'danger');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();

    const triggerPrint = () => {
      printWindow.print();
    };

    if (printWindow.document.readyState === 'complete') {
      triggerPrint();
      return;
    }

    printWindow.addEventListener('load', triggerPrint, { once: true });
  }

  private buildBillLineItems(party: 'Buyer' | 'Seller', selected: UnbilledRow): OaBillLineItem[] {
    const rows = this.chargeRows()
      .map((row) => {
        const amount = party === 'Buyer' ? this.buyerAmount(row) : this.sellerAmount(row);
        if (amount <= 0) return null;
        return {
          description: this.invoiceChargeDescription(row.chargeType),
          hsnSac: '27160000',
          quantity: selected.quantum > 0 ? selected.quantum.toFixed(2) : '-',
          rate: selected.quantum > 0 ? (amount / selected.quantum).toFixed(2) : '-',
          per: selected.quantum > 0 ? 'MWh' : '-',
          amount,
        } satisfies OaBillLineItem;
      })
      .filter((item): item is OaBillLineItem => !!item);

    return rows.length
      ? rows
      : [
          {
            description: `${party} OA Charges`,
            hsnSac: '27160000',
            quantity: selected.quantum > 0 ? selected.quantum.toFixed(2) : '-',
            rate: '0.00',
            per: selected.quantum > 0 ? 'MWh' : '-',
            amount: 0,
          },
        ];
  }

  private invoiceChargeDescription(chargeType: string): string {
    const label = String(chargeType || '').trim();
    if (!label) return 'OA Charges';
    if (/charges?$/i.test(label)) return label;
    return `${label} Charges`;
  }

  private parseLineItems(raw: unknown): OaBillLineItem[] {
    if (!Array.isArray(raw)) return [];
    return raw
      .map((item) => {
        if (!item || typeof item !== 'object') return null;
        const source = item as Record<string, unknown>;
        return {
          description: String(source['description'] || '-'),
          hsnSac: String(source['hsnSac'] || source['hsn_sac'] || '27160000'),
          quantity: String(source['quantity'] || '-'),
          rate: String(source['rate'] || '-'),
          per: String(source['per'] || '-'),
          amount: Number(source['amount']) || 0,
        } as OaBillLineItem;
      })
      .filter((item): item is OaBillLineItem => !!item);
  }

  private buildOaBillDocumentHtml(bill: OaGeneratedBill): string {
    const partyPan = bill.party === 'Buyer' ? bill.buyerPan : bill.sellerPan;
    const today = new Date();
    const lines = bill.lineItems.length
      ? bill.lineItems
      : [
          {
            description: `${bill.party.toUpperCase()} OA BILL FOR ${bill.source || 'OPEN ACCESS'} - APPLICATION ${bill.applicationId}`,
            hsnSac: '27160000',
            quantity: (Number(bill.quantum) || 0).toFixed(2),
            rate: Number(bill.quantum) > 0 ? (Number(bill.totalAmount) / Number(bill.quantum)).toFixed(2) : '0.00',
            per: Number(bill.quantum) > 0 ? 'MWh' : '-',
            amount: Number(bill.totalAmount) || 0,
          },
        ];
    const lineRows = lines
      .map(
        (line, index) => `<tr class="align-top">
          <td>${index + 1}</td>
          <td colspan="8">${this.escapeHtml(line.description)}</td>
          <td>${Number(line.amount).toFixed(2)}</td>
          <td colspan="2" style="text-align:end">Rs. ${Number(line.amount).toFixed(2)}</td>
        </tr>`,
      )
      .join('');

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${this.escapeHtml(bill.billId)} - Bill of Supply</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 18px; color: #111827; }
    .pdf-page { max-width: 960px; margin: 0 auto; }
    .pdf-hidden { display: block; }
    .heading { text-align: center; font-size: 18px; margin: 0 0 8px; font-weight: 700; }
    table { width: 100%; border-collapse: collapse; }
    .info-table td, .info-table th { border: 1px solid #2f3542; padding: 6px 7px; font-size: 12px; vertical-align: top; }
    .nested-table { width: 100%; border-collapse: collapse; }
    .nested-table td { border: 1px solid #2f3542; padding: 6px; font-size: 11px; vertical-align: top; width: 50%; }
    .nested-table th { display: none; }
    .text-center { text-align: center; }
    .text-end { text-align: right; }
    .logo-cell { min-width: 80px; }
    .invoice-logo { width: 100%; max-width: 150px; height: auto; display: block; margin: 0 auto; }
    .align-top { vertical-align: top; }
    .amount-words td { font-weight: 700; }
    .float-right { float: right; }
    .signature-box { min-height: 64px; }
    .fw-bold { font-weight: 700; }
    .fw-normal { font-weight: 400; }
    .small { font-size: 11px; line-height: 1.45; }
    .mt-2 { margin-top: 10px; }
    .pt-2 { padding-top: 8px; }
    .my-2 { margin-top: 8px; margin-bottom: 8px; }
    .ms-auto { margin-left: auto; }
    .p-1 { padding: 4px; }
    .p-2 { padding: 8px; }
    .border { border: 1px solid #2f3542; }
    .border-top { border-top: 1px solid #2f3542; }
    .col-5 { width: 280px; margin-left: auto; }
    .offset-7 { margin-left: auto; }
  </style>
</head>
<body>
  <div class="pdf-page pdf-hidden" id="pdfContent">
    <div id="formatD" class="pdf-page">
      <h5 class="heading text-center mb-0">Bill of Supply</h5>
      <table class="info-table">
        <colgroup>
          <col style="width: 5.5%" />
          <col style="width: 5.5%" />
          <col style="width: 8%" />
          <col style="width: 8%" />
          <col style="width: 8%" />
          <col style="width: 8%" />
          <col style="width: 8%" />
          <col style="width: 8%" />
          <col style="width: 8%" />
          <col style="width: 11%" />
          <col style="width: 11%" />
          <col style="width: 11%" />
        </colgroup>
        <tr>
          <td colspan="2" class="w-10 align-middle text-center logo-cell"><img src="${this.escapeHtml(this.invoiceLogoUrl)}" alt="Sharajman" class="invoice-logo"></td>
          <td colspan="7" class="w-40 align-top p-2">
            <strong>Address</strong><br>
            ${this.escapeHtml(this.issuerProfile.companyName)}<br>
            ${this.escapeHtml(this.issuerProfile.address)}<br>
            <strong>PAN:</strong> ${this.escapeHtml(this.issuerProfile.pan)}<br>
            <strong>GSTIN/UIN:</strong> ${this.escapeHtml(this.issuerProfile.gst)}<br>
            <strong>State:</strong> ${this.escapeHtml(this.issuerProfile.state)} <strong>Code:</strong> ${this.escapeHtml(this.issuerProfile.stateCode)}<br>
            <strong>CIN:</strong> ${this.escapeHtml(this.issuerProfile.cin)}<br>
          </td>
          <td colspan="3" class="p-1">
            <table class="nested-table">
              <tr>
                <th scope="row">Invoice and Billing Date</th>
                <td><span class="fw-bold">Invoice</span><br>${this.escapeHtml(bill.billId || '\u00A0')}</td>
                <td><span class="fw-bold">Billing Date</span><br>${this.escapeHtml(this.formatShortMonthDate(today))}</td>
              </tr>
              <tr>
                <th scope="row">Due Date and Financial Year</th>
                <td><span class="fw-bold">Due Date</span><br>${this.escapeHtml(this.formatNumericDate(this.addDays(today, 7)))}</td>
                <td><span class="fw-bold">Financial Year</span><br>${this.escapeHtml(this.currentFinancialYear(today))}</td>
              </tr>
              <tr>
                <th scope="row">From Date and To Date</th>
                <td><span class="fw-bold">From Date</span><br>${this.escapeHtml(bill.fromDate || '-')}</td>
                <td><span class="fw-bold">To Date</span><br>${this.escapeHtml(bill.toDate || '-')}</td>
              </tr>
            </table>
          </td>
        </tr>
        <tr>
          <td colspan="9" class="align-top p-2">
            <strong>Cosignee (Ship To)</strong><br>
            ${this.escapeHtml(bill.seller)}<br>
            ${this.escapeHtml(bill.sellerAddress)}<br>
            <strong>GSTIN:</strong> ${this.escapeHtml(bill.sellerGst)}<br>
            <strong>PAN:</strong> ${this.escapeHtml(bill.sellerPan)}<br>
            <strong>State:</strong> ${this.escapeHtml(bill.sellerState)} <strong>Code:</strong> -<br>
            <strong>Place of Supply:</strong> ${this.escapeHtml(bill.sellerState)}
          </td>
          <td colspan="3" class="align-top p-2">
            <strong>Buyer (Bill To)</strong><br>
            ${this.escapeHtml(bill.buyer)}<br>
            ${this.escapeHtml(bill.buyerAddress)}<br>
            <strong>GSTIN:</strong> ${this.escapeHtml(bill.buyerGst)}<br>
            <strong>PAN:</strong> ${this.escapeHtml(bill.buyerPan)}<br>
            <strong>State:</strong> ${this.escapeHtml(bill.buyerState)} <strong>Code:</strong> -<br>
            <strong>Place of Supply:</strong> ${this.escapeHtml(bill.buyerState)}
          </td>
        </tr>
        <tr class="align-top text-center">
          <th id="sn">S.No.</th>
          <th colspan="8" id="partyname">Description</th>
          <th id="rate">Rate(Rs)</th>
          <th class="text-end" colspan="2" id="partyamt">Amount</th>
        </tr>
        ${lineRows}
        <tr>
          <td colspan="10"><strong>Total</strong></td>
          <td colspan="2" style="text-align:end"><strong>Rs. ${Number(bill.totalAmount).toFixed(2)}</strong></td>
        </tr>
        <tr class="amount-words">
          <td colspan="12">
            <strong>Amount Chargeable (in words)</strong>
            <span class="float-right">E. & O.E</span>
          </td>
        </tr>
        <tr>
          <td colspan="12">INR ${this.escapeHtml(this.numberToWordsIndian(Math.round(Number(bill.totalAmount) || 0)))} Rupees Only</td>
        </tr>
        <tr>
          <th id="taxable" colspan="8">Taxable Value</th>
          <th id="taxable" colspan="4">Amount</th>
        </tr>
        <tr>
          <td id="taxable" colspan="8">Open Access Charges</td>
          <td id="taxable" colspan="4" style="text-align:end">${Number(bill.totalAmount).toFixed(2)}</td>
        </tr>
        <tr>
          <td id="taxable" colspan="8">Total</td>
          <td id="taxable" colspan="4" style="text-align:end">Rs. ${Number(bill.totalAmount).toFixed(2)}</td>
        </tr>
        <tr>
          <td colspan="8">
            <strong>Total Amount (in Words):</strong> INR ${this.escapeHtml(this.numberToWordsIndian(Math.round(Number(bill.totalAmount) || 0)))} Rupees Only<br>
            <strong>Company's PAN:</strong> ${this.escapeHtml(partyPan)}
          </td>
          <td colspan="4" rowspan="2">
            <strong>Company's Bank Details</strong><br>
            <b>A/c Holder's Name</b> : ${this.escapeHtml(this.bankProfile.accountHolderName)}<br>
            <b>Bank Name</b> : ${this.escapeHtml(this.bankProfile.bankName)}<br>
            <b>A/c No.</b> : ${this.escapeHtml(this.bankProfile.accountNumber)}<br>
            <b>Branch & IFSC Code</b> : ${this.escapeHtml(this.bankProfile.branch)} & ${this.escapeHtml(this.bankProfile.ifscCode)}<br>
            <b>SWIFT Code</b> : ${this.escapeHtml(this.bankProfile.swiftCode)}
          </td>
        </tr>
        <tr>
          <td colspan="8">
            <strong>Declaration</strong><br>
            We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.
          </td>
        </tr>
      </table>
      <div class="mt-2">
        <div class="col-5 offset-7 border">
          <div class="fw-bold text-center">${this.escapeHtml(this.issuerProfile.companyName)}</div>
          <div class="signature-box my-2 ms-auto"></div>
          <div class="fw-bold text-center">Authorised Signatory</div>
        </div>
      </div>
      <div class="row border-top mt-2 pt-2 text-center small fw-bold">
        <div>
          Corporate Address : 6th Floor, B Wing, Statesman House, Barakhamba Road, Connaught Place, Central Delhi, New Delhi, Delhi, India, 110001
        </div>
        <div class="fw-normal">
          Registered Address : "Statesman House", Barakhamba Road, Connaught Place, Central Delhi, New Delhi, Delhi, India, 110001
        </div>
        <div class="fw-normal">
          CIN: U45208JH2005PLC011713 | Tel: +91-334061 0165 | Email: feedback@manikaranpowerltd.in | www.manikaranpowerltd.in
        </div>
      </div>
    </div>
  </div>
</body>
</html>`;
  }

  private formatShortMonthDate(value: Date): string {
    return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }).format(value);
  }

  private formatNumericDate(value: Date): string {
    const day = String(value.getDate()).padStart(2, '0');
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const year = String(value.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  }

  private startOfDay(value: Date): Date {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  private addDays(value: Date, days: number): Date {
    const result = this.startOfDay(value);
    result.setDate(result.getDate() + days);
    return result;
  }

  private currentFinancialYear(value: Date): string {
    const year = value.getFullYear();
    const month = value.getMonth();
    const startYear = month >= 3 ? year : year - 1;
    const endYear = startYear + 1;
    return `${startYear}-${String(endYear).slice(-2)}`;
  }

  private periodDateRange(period: string): { from: string; to: string } {
    const parts = (period || '')
      .split('to')
      .map((item) => item.trim())
      .filter(Boolean);
    if (parts.length >= 2) {
      return { from: parts[0], to: parts.slice(1).join(' to ') };
    }
    return { from: period || '-', to: period || '-' };
  }

  private escapeHtml(value: string): string {
    return (value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private numberToWordsIndian(value: number): string {
    if (!Number.isFinite(value) || value <= 0) return 'Zero';

    const ones = [
      '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
      'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
    ];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const belowThousand = (num: number): string => {
      const hundred = Math.floor(num / 100);
      const rem = num % 100;
      const head = hundred ? `${ones[hundred]} Hundred` : '';
      if (!rem) return head || 'Zero';
      if (rem < 20) return `${head} ${ones[rem]}`.trim();
      return `${head} ${tens[Math.floor(rem / 10)]} ${ones[rem % 10]}`.trim();
    };

    const crore = Math.floor(value / 10000000);
    const lakh = Math.floor((value % 10000000) / 100000);
    const thousand = Math.floor((value % 100000) / 1000);
    const rest = value % 1000;

    const parts: string[] = [];
    if (crore) parts.push(`${belowThousand(crore)} Crore`);
    if (lakh) parts.push(`${belowThousand(lakh)} Lakh`);
    if (thousand) parts.push(`${belowThousand(thousand)} Thousand`);
    if (rest) parts.push(belowThousand(rest));
    return parts.join(' ').replace(/\s+/g, ' ').trim();
  }

  private nextOaBillSequence(): number {
    return this.generatedBills()
      .map((item) => {
        const parts = String(item.billId || '').split('/');
        const serial = Number(parts[2] || '');
        return Number.isFinite(serial) ? serial : 0;
      })
      .reduce((max, serial) => Math.max(max, serial), 0) + 1;
  }

  private buildOaBillId(serial: number): string {
    return `OA/${this.oaFinancialYearCode(new Date())}/${String(serial).padStart(4, '0')}`;
  }

  private oaFinancialYearCode(value: Date): string {
    const year = value.getFullYear();
    const month = value.getMonth();
    const startYear = month >= 3 ? year : year - 1;
    const endYear = startYear + 1;
    return `${String(startYear).slice(-2)}-${String(endYear).slice(-2)}`;
  }
}
